import React, { useState, useMemo } from 'react';
import { IconTarget, IconTrendingUp, IconTrendingDown } from '../components/Icons';
import { API_BASE_URL } from '../../config';

interface DCFInputs {
  ticker: string;
  revenueBase: number;
  growthRates: [number, number, number, number, number];
  ebitdaMargin: number;
  capexPct: number;
  taxRate: number;
  wacc: number;
  terminalGrowth: number;
  sharesOutstanding: number;
  currentPrice: number;
}

interface ProjectionRow {
  year: number;
  revenue: number;
  ebitda: number;
  fcf: number;
  discountFactor: number;
  pvFcf: number;
}

interface DCFResults {
  rows: ProjectionRow[];
  terminalValue: number;
  terminalNPV: number;
  totalNPV: number;
  intrinsicPerShare: number;
}

function computeDCF(inputs: DCFInputs): DCFResults {
  const { revenueBase, growthRates, ebitdaMargin, capexPct, taxRate, wacc, terminalGrowth, sharesOutstanding } = inputs;
  const w = wacc / 100;
  const tg = terminalGrowth / 100;
  const em = ebitdaMargin / 100;
  const cp = capexPct / 100;
  const tr = taxRate / 100;

  const rows: ProjectionRow[] = [];
  let revenuePrev = revenueBase;

  for (let i = 1; i <= 5; i++) {
    const gRate = growthRates[i - 1] / 100;
    const revenue = revenuePrev * (1 + gRate);
    const ebitda = revenue * em;
    const fcf = ebitda * (1 - tr) - revenue * cp;
    const discountFactor = Math.pow(1 + w, i);
    const pvFcf = fcf / discountFactor;
    rows.push({ year: i, revenue, ebitda, fcf, discountFactor, pvFcf });
    revenuePrev = revenue;
  }

  const fcfN = rows[4].fcf;
  const terminalValue = w > tg ? (fcfN * (1 + tg)) / (w - tg) : 0;
  const terminalNPV = terminalValue / Math.pow(1 + w, 5);
  const totalNPV = rows.reduce((sum, r) => sum + r.pvFcf, 0) + terminalNPV;
  const intrinsicPerShare = sharesOutstanding > 0 ? totalNPV / sharesOutstanding : 0;

  return { rows, terminalValue, terminalNPV, totalNPV, intrinsicPerShare };
}

function buildSensitivity(baseInputs: DCFInputs, waccSteps: number[], tgSteps: number[]): number[][] {
  return waccSteps.map(w =>
    tgSteps.map(tg => computeDCF({ ...baseInputs, wacc: w, terminalGrowth: tg }).intrinsicPerShare)
  );
}

const fmt = (n: number, d = 2) => n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtM = (n: number) => fmt(n, 1);

function sensitivityCellClass(value: number, currentPrice: number, isSelected: boolean): string {
  if (isSelected) return 'bg-amber-200 text-amber-900 font-bold';
  if (currentPrice <= 0) return 'bg-slate-50 text-slate-700';
  const ratio = value / currentPrice;
  if (ratio > 1.1) return 'bg-emerald-100 text-emerald-800';
  if (ratio >= 0.9) return 'bg-amber-50 text-amber-800';
  return 'bg-red-100 text-red-800';
}

interface SliderRowProps {
  label: string; value: number; min: number; max: number; step: number; unit?: string;
  onChange: (v: number) => void;
}
const SliderRow: React.FC<SliderRowProps> = ({ label, value, min, max, step, unit = '%', onChange }) => (
  <div className="space-y-1">
    <div className="flex justify-between items-center">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{value.toFixed(1)}{unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-amber-500"
    />
    <div className="flex justify-between text-[10px] text-slate-400"><span>{min}{unit}</span><span>{max}{unit}</span></div>
  </div>
);

const ValuationView: React.FC = () => {
  const [ticker, setTicker] = useState('');
  const [revenueBase, setRevenueBase] = useState(1000);
  const [growthRates, setGrowthRates] = useState<[number,number,number,number,number]>([15,13,11,9,7]);
  const [ebitdaMargin, setEbitdaMargin] = useState(25);
  const [capexPct, setCapexPct] = useState(5);
  const [taxRate, setTaxRate] = useState(25);
  const [wacc, setWacc] = useState(10);
  const [terminalGrowth, setTerminalGrowth] = useState(2.5);
  const [sharesOutstanding, setSharesOutstanding] = useState(100);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadFromYahoo = async () => {
    if (!ticker) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/aiq/valuation/${ticker}`);
      if (!res.ok) {
        const err = await res.json();
        setLoadError(err.error || 'Failed to load data');
        return;
      }
      const d = await res.json();
      setRevenueBase(d.revenueBase);
      setGrowthRates(d.growthRates as [number,number,number,number,number]);
      setEbitdaMargin(d.ebitdaMargin);
      setCapexPct(d.capexPct);
      setWacc(d.wacc);
      setSharesOutstanding(d.sharesOutstanding);
      setCurrentPrice(d.currentPrice);
      setDataSource(`${d.name} · Yahoo Finance`);
      setHasCalculated(false);
    } catch {
      setLoadError('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  const inputs: DCFInputs = { ticker, revenueBase, growthRates, ebitdaMargin, capexPct, taxRate, wacc, terminalGrowth, sharesOutstanding, currentPrice };

  const results = useMemo<DCFResults | null>(() => {
    if (!hasCalculated) return null;
    return computeDCF(inputs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCalculated, revenueBase, growthRates, ebitdaMargin, capexPct, taxRate, wacc, terminalGrowth, sharesOutstanding, currentPrice]);

  const waccSteps = useMemo(() =>
    [wacc-2, wacc-1, wacc, wacc+1, wacc+2].map(v => parseFloat(Math.max(1, v).toFixed(1))), [wacc]);
  const tgSteps = useMemo(() =>
    [terminalGrowth-1, terminalGrowth-0.5, terminalGrowth, terminalGrowth+0.5, terminalGrowth+1]
      .map(v => parseFloat(Math.max(0, Math.min(10, v)).toFixed(1))), [terminalGrowth]);

  const sensitivityGrid = useMemo(() => {
    if (!hasCalculated) return null;
    return buildSensitivity(inputs, waccSteps, tgSteps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCalculated, waccSteps, tgSteps, revenueBase, growthRates, ebitdaMargin, capexPct, sharesOutstanding]);

  const upside = results && currentPrice > 0
    ? ((results.intrinsicPerShare - currentPrice) / currentPrice) * 100 : null;

  const setGrowthRate = (idx: number, v: number) => {
    setGrowthRates(prev => { const n = [...prev] as [number,number,number,number,number]; n[idx] = v; return n; });
    setHasCalculated(false);
  };

  return (
    <div className="flex h-full bg-slate-100">
      {/* Left Sidebar */}
      <div className="w-80 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-y-auto custom-scrollbar">
        <div className="px-5 py-4 border-b border-slate-100 bg-amber-50">
          <div className="flex items-center gap-2 mb-1">
            <IconTarget className="text-amber-600" size={18} />
            <h2 className="text-base font-bold text-slate-800">DCF Valuation</h2>
          </div>
          <p className="text-xs text-slate-500">Intrinsic Value Calculator</p>
          {dataSource && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-medium text-emerald-700 truncate">Live · {dataSource}</span>
            </div>
          )}
        </div>

        <div className="flex-1 px-5 py-4 space-y-5">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">Ticker Symbol</label>
            <div className="flex gap-2">
              <input type="text" value={ticker}
                onChange={e => { setTicker(e.target.value.toUpperCase()); setDataSource(null); setLoadError(null); setHasCalculated(false); }}
                onKeyDown={e => e.key === 'Enter' && loadFromYahoo()}
                placeholder="e.g. AAPL"
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono tracking-widest uppercase"
              />
              <button onClick={loadFromYahoo} disabled={!ticker || isLoading}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap">
                {isLoading ? '…' : 'Load'}
              </button>
            </div>
            {loadError && <p className="text-[11px] text-red-500 mt-1">{loadError}</p>}
            {dataSource && !loadError && <p className="text-[11px] text-emerald-600 mt-1">Inputs pre-filled from Yahoo Finance</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">Revenue Base ($M)</label>
            <input type="number" value={revenueBase}
              onChange={e => { setRevenueBase(parseFloat(e.target.value)||0); setHasCalculated(false); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Revenue Growth (Year 1–5)</p>
            {growthRates.map((rate, idx) => (
              <SliderRow key={idx} label={`Year ${idx+1}`} value={rate} min={0} max={50} step={0.5}
                onChange={v => setGrowthRate(idx, v)} />
            ))}
          </div>

          <SliderRow label="EBITDA Margin %" value={ebitdaMargin} min={5} max={60} step={0.5}
            onChange={v => { setEbitdaMargin(v); setHasCalculated(false); }} />
          <SliderRow label="CapEx % of Revenue" value={capexPct} min={0} max={30} step={0.5}
            onChange={v => { setCapexPct(v); setHasCalculated(false); }} />
          <SliderRow label="Effective Tax Rate" value={taxRate} min={0} max={40} step={0.5}
            onChange={v => { setTaxRate(v); setHasCalculated(false); }} />
          <SliderRow label="WACC %" value={wacc} min={5} max={20} step={0.5}
            onChange={v => { setWacc(v); setHasCalculated(false); }} />
          <SliderRow label="Terminal Growth %" value={terminalGrowth} min={0} max={5} step={0.1}
            onChange={v => { setTerminalGrowth(v); setHasCalculated(false); }} />

          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">Shares Outstanding (M)</label>
            <input type="number" value={sharesOutstanding}
              onChange={e => { setSharesOutstanding(parseFloat(e.target.value)||1); setHasCalculated(false); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">Current Market Price ($)</label>
            <input type="number" value={currentPrice}
              onChange={e => { setCurrentPrice(parseFloat(e.target.value)||0); setHasCalculated(false); }}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100">
          <button onClick={() => setHasCalculated(true)}
            className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm">
            <IconTarget size={15} /> Calculate Valuation
          </button>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
        {!hasCalculated || !results ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-2xl flex items-center justify-center mb-4">
              <IconTarget className="text-amber-400" size={28} />
            </div>
            <p className="text-base font-semibold text-slate-600 mb-1">Ready to Calculate</p>
            <p className="text-sm text-slate-400 max-w-xs">Enter your assumptions in the left panel and click <span className="font-semibold text-amber-600">Calculate Valuation</span>.</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <p className="text-xs font-medium text-slate-500 mb-1">Intrinsic Value / Share</p>
                <p className={`text-2xl font-bold ${currentPrice > 0 ? (results.intrinsicPerShare >= currentPrice ? 'text-emerald-600' : 'text-red-600') : 'text-amber-600'}`}>
                  ${fmt(results.intrinsicPerShare)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total NPV ${fmtM(results.totalNPV)}M</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <p className="text-xs font-medium text-slate-500 mb-1">Current Market Price</p>
                <p className="text-2xl font-bold text-slate-700">{currentPrice > 0 ? `$${fmt(currentPrice)}` : '—'}</p>
                <p className="text-xs text-slate-400 mt-1">{currentPrice > 0 ? 'Market reference' : 'Enter in left panel'}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <p className="text-xs font-medium text-slate-500 mb-1">Upside / Downside</p>
                {upside !== null ? (
                  <div className={`flex items-center gap-1 ${upside >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {upside >= 0 ? <IconTrendingUp size={20} /> : <IconTrendingDown size={20} />}
                    <span className="text-2xl font-bold">{upside >= 0 ? '+' : ''}{fmt(upside)}%</span>
                  </div>
                ) : <p className="text-2xl font-bold text-slate-300">—</p>}
                <p className="text-xs text-slate-400 mt-1">{upside !== null ? (upside >= 0 ? 'Undervalued vs market' : 'Overvalued vs market') : 'Requires current price'}</p>
              </div>
            </div>

            {/* Projection Table */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-700">DCF Projection Table</h4>
                <p className="text-xs text-slate-400">5-year free cash flow projection · {taxRate}% effective tax rate</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {['Year','Revenue ($M)','EBITDA ($M)','FCF ($M)','Discount Factor','PV of FCF ($M)'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((row, i) => (
                      <tr key={row.year} className={`border-b border-slate-100 ${i%2===0?'bg-white':'bg-slate-50'} hover:bg-amber-50`}>
                        <td className="px-4 py-2.5 font-semibold text-amber-700">Year {row.year}</td>
                        <td className="px-4 py-2.5 text-slate-700">${fmtM(row.revenue)}M</td>
                        <td className="px-4 py-2.5 text-slate-700">${fmtM(row.ebitda)}M</td>
                        <td className={`px-4 py-2.5 font-medium ${row.fcf>=0?'text-emerald-600':'text-red-600'}`}>{row.fcf<0?'-':''}${fmtM(Math.abs(row.fcf))}M</td>
                        <td className="px-4 py-2.5 text-slate-500">{row.discountFactor.toFixed(4)}</td>
                        <td className={`px-4 py-2.5 font-medium ${row.pvFcf>=0?'text-emerald-600':'text-red-600'}`}>{row.pvFcf<0?'-':''}${fmtM(Math.abs(row.pvFcf))}M</td>
                      </tr>
                    ))}
                    <tr className="bg-amber-50 border-t-2 border-amber-200">
                      <td className="px-4 py-2.5 font-bold text-amber-800">Terminal Value</td>
                      <td colSpan={3} className="px-4 py-2.5 text-slate-400 text-xs italic">—</td>
                      <td className="px-4 py-2.5 text-slate-400 text-xs">{Math.pow(1+wacc/100,5).toFixed(4)}</td>
                      <td className="px-4 py-2.5 font-bold text-amber-700">${fmtM(results.terminalNPV)}M</td>
                    </tr>
                    <tr className="bg-amber-100 border-t border-amber-300">
                      <td colSpan={5} className="px-4 py-2.5 font-bold text-amber-900 text-right pr-6">Total NPV (Enterprise Value)</td>
                      <td className="px-4 py-2.5 font-bold text-amber-800 text-base">${fmtM(results.totalNPV)}M</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sensitivity Table */}
            {sensitivityGrid && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700">Sensitivity — Intrinsic Value/Share ($)</h4>
                  <p className="text-xs text-slate-400">WACC (rows) × Terminal Growth Rate (columns)</p>
                </div>
                <div className="overflow-x-auto p-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200">WACC \ TGR</th>
                        {tgSteps.map(tg => (
                          <th key={tg} className={`px-3 py-2 text-xs font-semibold text-center border border-slate-200 ${tg===parseFloat(terminalGrowth.toFixed(1))?'bg-amber-100 text-amber-800':'bg-slate-50 text-slate-500'}`}>{tg.toFixed(1)}%</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {waccSteps.map((w, wi) => (
                        <tr key={w}>
                          <td className={`px-3 py-2 text-xs font-semibold border border-slate-200 ${w===parseFloat(wacc.toFixed(1))?'bg-amber-100 text-amber-800':'bg-slate-50 text-slate-500'}`}>{w.toFixed(1)}%</td>
                          {tgSteps.map((tg, ti) => {
                            const val = sensitivityGrid[wi][ti];
                            const isSelected = w===parseFloat(wacc.toFixed(1)) && tg===parseFloat(terminalGrowth.toFixed(1));
                            return (
                              <td key={tg} className={`px-3 py-2 text-xs text-center border border-slate-200 ${sensitivityCellClass(val, currentPrice, isSelected)}`}>
                                ${fmt(val)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                  <span>WACC: <strong className="text-slate-700">{wacc.toFixed(1)}%</strong></span>
                  <span>TGR: <strong className="text-slate-700">{terminalGrowth.toFixed(1)}%</strong></span>
                  <span>EBITDA Margin: <strong className="text-slate-700">{ebitdaMargin.toFixed(1)}%</strong></span>
                  <span>CapEx/Rev: <strong className="text-slate-700">{capexPct.toFixed(1)}%</strong></span>
                  <span>Shares: <strong className="text-slate-700">{sharesOutstanding.toFixed(1)}M</strong></span>
                  <span>Tax Rate: <strong className="text-slate-700">{taxRate.toFixed(1)}%</strong></span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ValuationView;
